import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import Button from "../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../components/card/card.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import vmsService from "../../../services/web/vms-service.js";
import {useQuery} from "@tanstack/react-query";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import TableCameras from "./components/table-cameras.jsx";
import VmsModal from "./components/vms-modal.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import projectUtils from "../../../utils/project-utils.js";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilter = {cameraName: "", labels: ""};
const filterGlobal = {...initialFilter};

const VideoCamerasList = () => {
    const [cameras, setCameras] = useState([]);
    const [filter, setFilter] = useState(filterGlobal);
    const [vmsModal, setVmsModal] = useState({open: false, target: "camera"})

    const gridRef = useRef();

    const {data, isSuccess, isLoading: isLoadingCameras} = useQuery({
        queryFn: () => vmsService.list(),
        queryKey: ["vms-cameras"]
    })

    const {data: vmsData, isLoading: isLoadingVmsData} = useQuery({
        queryFn: () => vmsService.getAll(),
        queryKey: ["vms-cameras-all"]
    });

    useEffect(() => {
        if (!isSuccess) return;

        setCameras(data)
    }, [isSuccess]);

    const openVms = (target) => {
        if (vmsData.length > 1) {
            setVmsModal({open: true, target})
            return;
        }

        const vmsTab = window.open("", "_blank");
        vmsTab.opener = null;
        axios.get(`${API_URL}/vms/${target === "camera" ? 'access-from-video-camera' : 'access-from-collection-system'}/${vmsData[0].id}`)
            .then(({data}) => vmsTab.location.href = data)
            .catch(() => {
                vmsTab.close();
                projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS.", "Ok");
            })
    }

    const handleInputs = ({target: {id, value}}) => setFilter(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => {
        setCameras(
            data.filter((item) =>
                Object.entries(filter).every(([key, value]) => {
                    if (!value) return true;

                    const normalizedFilter = normalizeFilterText(value);

                    if (key === "cameraName") {
                        return [
                            item.cameraName,
                            item.dvrName,
                        ]
                            .filter(Boolean)
                            .some((field) =>
                                normalizeFilterText(field).includes(normalizedFilter)
                            );
                    }

                    return normalizeFilterText(item[key]).includes(normalizedFilter);
                })
            )
        );
    }, [data, filter]);

    const clear = () => {
        setFilter(initialFilter);
        setCameras(data);
    }

    const normalizeFilterText = (text = "") => {
        if (!text || (typeof text !== "string")) return "";

        return text
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, "")
            .toLowerCase();
    }

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <>
            <Template isLoading={isLoadingCameras || isLoadingVmsData}>
                <Template.Header>
                    <Template.Title browserTitle="Câmeras de OCR">
                        Câmeras
                    </Template.Title>

                    { vmsData?.length > 0 &&
                        <Button onClick={() => openVms("camera")}>
                            <FaPlus/>
                            Adicionar câmera
                        </Button>
                    }
                </Template.Header>

                <Template.Body>
                    <Card>
                        <Card.Body>
                            <SentryRow>
                                <div className="col-span-8">
                                    <InputForm id="cameraName" value={filter.cameraName} onChange={handleInputs} label="Pesquisar"/>
                                </div>
                                <div className="col-span-4 flex gap-4">
                                    <InputForm id="labels" value={filter.labels} onChange={handleInputs} label="Marcadores" classNameWrapper={"w-full"}/>
                                    <div className={"flex gap-2"}>
                                        <Button title="Limpar" onClick={clear} target={"primary-outline"}>
                                            <FaEraser />
                                        </Button>
                                        <Button title="Buscar" onClick={apply}>
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <Card className={"mt-2"}>
                        <Card.Body>
                            <TableCameras cameras={cameras} initialFilter={filterGlobal} gridRef={gridRef}/>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <VmsModal vmsModal={vmsModal} data={vmsData} setVmsModal={setVmsModal}/>
        </>
    );
};

export default VideoCamerasList;